// React Components
import { useState, useEffect, useContext, useMemo } from "react";
import partCodesService from "services/part-code-service";
import changeLogService from "services/change-log-service";
import { AuthContext } from "context";

// MUI & Material Dashboard components
import MDTypography from "components/MDTypography";
import MDBox from "components/MDBox";
import MDDropzone from "components/MDDropzone";
import MDButton from "components/MDButton";
import MDAvatar from "components/MDAvatar";
import DataTable from "e4acomponents/Tables/DataTable";
import DeleteIcon from "@mui/icons-material/Delete";
import DownloadIcon from '@mui/icons-material/Download';
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Grid from "@mui/material/Grid";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";

// File icons
import pdfThumbnail from "assets/images/thumbnails/pdf.png";
import docxThumbnail from "assets/images/thumbnails/docx.png";
import xlsThumbnail from "assets/images/thumbnails/xls.png";
import pptxThumbnail from "assets/images/thumbnails/pptx.png";
import stepThumbnail from "assets/images/thumbnails/step.png";

export default function EditFiles({ part, setPart }) {

    // Stores the DataTable's rows
    const [tableData, setTableData] = useState([]);
    const [newFiles, setNewFiles] = useState({});
    const [openAddFilesAlert, setOpenAddFilesAlert] = useState(false);
    const [openDeleteFileAlert, setOpenDeleteFileAlert] = useState(false);

    const authContext = useContext(AuthContext);

    // Stores the file icons
    const fileIcons = {
        'pdf': pdfThumbnail,
        'doc': docxThumbnail,
        'docx': docxThumbnail,
        'xls': xlsThumbnail,
        'xlsx': xlsThumbnail,
        'ppt': pptxThumbnail,
        'pptx': pptxThumbnail,
    };

    // Generates the rows for the DataTable
    // Note: Avoid using object references in deps and avoid calling setPart here to prevent render loops.
    useEffect(() => {
        const files = part.filePaths || {};
        const count = Object.keys(files).length;

        if (part.has_files && count > 0) {
            setTableData(getRows(files));
        } else {
            // Keep local table empty without mutating parent state
            setTableData([]);
        }

        // Only re-run when the boolean has_files changes or the number of files changes
    }, [part.has_files, part.filePaths ? Object.keys(part.filePaths).length : 0]);

    // Helper that selects and returns the correct icon for the file
    const selectIcon = (filename) => {
        let ext = filename.split('.').pop();
        if (fileIcons[ext]) {
            return fileIcons[ext];
        }
        return stepThumbnail;
    }

    // Generates the rows for the DataTable
    const getRows = (info) => {
        let updatedInfo = Object.entries(info)
            .map(([key, value], index) => {
                return {
                    index: index + 1,
                    filename: key,
                    filekey: value,
                };
            });
        return updatedInfo;
    }

    // Adds the dropzone files to the newFiles state
    const onFileAdd = async (file) => {
        const reader = new FileReader();
        reader.onload = (e) => {
            setNewFiles(prev => ({
                ...prev,
                [file.upload.filename]: e.target.result
            }));
        };
        reader.readAsDataURL(file);
    }

    // Upload files to the server and update the partcode's filepaths
    const uploadFiles = async () => {
        if (Object.keys(newFiles).length === 0) {
            return; // No files to upload
        }
        let newFilepaths = { ...part.filePaths };
        // Upload each file to the server
        const uploadPromises = Object.entries(newFiles).map(async ([key, value]) => {
            const payload = {
                data: {
                    type: "file",
                    id: part.id,
                    attributes: {
                        filename: key,
                        dataUrl: value,
                        folderpath: part.generatedUID
                    }
                }
            };
            const response = await partCodesService.uploadFile(payload);
            newFilepaths[key] = response.s3Key;
        });
        // Wait for all files to be uploaded
        await Promise.all(uploadPromises);

        // Capture the file changes in the changelog
        const changeLog = {
            data: {
                attributes: {
                    partCode: part.id,
                    change: {
                        type: "Attachments Change",
                        description: "Part code attachments have been changed",
                        date: new Date(),
                        user: await authContext.getCurrentUser(),
                    }
                }
            }
        }
        await changeLogService.addChange(changeLog);

        // Update the partcode's filepaths in the state
        setPart({ ...part, filePaths: newFilepaths, has_files: true });
        setNewFiles({});

        const payload = {
            data: {
                type: "files",
                id: part.id,
                attributes: { filePaths: newFilepaths}
            }
        }


        try {
            // Update the partcode's filepaths in the database
            await partCodesService.updatePartKeys(payload, part.id);
            setOpenAddFilesAlert(true);

        } catch (error) {
            console.log("Error updating partcode filepaths", error);
        }
    }

    // Deletes a file from s3 using its s3key and updates the partcode's filepaths
    const deleteFile = async (filekey, filename) => {
        try {
            if (!confirm("Are you sure you want to delete this file?")) {
                return;
            } else {
                // Remove the file from the s3 bucket
                await partCodesService.deleteFile(filekey);

                // Remove the file from the partcode's filepaths
                let updatedFiles = { ...part.filePaths };
                delete updatedFiles[filename];

                setPart({ ...part, filePaths: updatedFiles, has_files: Object.keys(updatedFiles).length > 0 });

                // Update the partcode's filepaths in the database
                const payload = {
                    data: {
                        type: "files",
                        id: part.id,
                        attributes: {
                            filePaths: updatedFiles
                        }
                    }
                }
                await partCodesService.updatePartKeys(payload, part.id);
                setOpenDeleteFileAlert(true);
            }
        } catch (error) {
            console.log(error);
        }

    };

    // DataTable containing the files and action on files
    const dataTableData = {
        columns: [
            { Header: "#", accessor: "index", width: "5%" },
            {
                Header: "Type",
                disableSortBy: true,
                accessor: "type",
                width: "15%",
                Cell: (row) => {
                    return (
                        <MDBox>
                            <MDAvatar src={selectIcon(row.row.original.filename)} alt="file icon" size="xs" variant="rounded" />
                        </MDBox>
                    )
                },
            },
            { Header: "File Name", accessor: "filename", width: "60%" },
            {
                Header: "Actions",
                disableSortBy: true,
                accessor: "actions",
                width: "20%",
                Cell: (row) => {
                    return (
                        <MDBox display="flex" alignItems="center">
                            <Tooltip
                                title="Delete File"
                                onClick={() => deleteFile(row.row.original.filekey, row.row.original.filename)}
                            >
                                <IconButton>
                                    <DeleteIcon />
                                </IconButton>
                            </Tooltip>
                            <Tooltip
                                title="Download File"
                            >
                                <IconButton>
                                    <DownloadIcon />
                                </IconButton>
                            </Tooltip>
                        </MDBox>
                    )
                }
            }
        ],
        rows: tableData
    }

    return (
        <MDBox mt={1}>
            {/* Part Code Accepted Alert */}
            <Snackbar
                open={openAddFilesAlert }
                autoHideDuration={1500}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                onClose={() => setOpenAddFilesAlert(false)}
            >
                <Alert severity="success" >
                    Files uploaded successfully!
                </Alert>
            </Snackbar>
            {/* Part Code Deleted Alert */}
            <Snackbar
                open={openDeleteFileAlert}
                autoHideDuration={1500}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                onClose={() => setOpenDeleteFileAlert(false)}
            >
                <Alert severity="error" >
                    File deleted successfully!
                </Alert>
            </Snackbar>

            {/*Delete/Download Files Section (DataTable)*/}
            {part.has_files && (
                <>
                    <MDTypography variant="h5" fontWeight="bold" mt={1} mb={2}>
                        Edit Files
                    </MDTypography>
                    <DataTable table={dataTableData} tableId="edit-files-table" />
                </>
            )}

            {/*Upload Files Section */}
            <MDTypography variant="h5" fontWeight="bold" mt={1} mb={2}>
                Upload Files
            </MDTypography>
            <Grid container spacing={2} direction="row">
                <Grid item xs={9} sm={9}>
                    {useMemo(
                        () => (
                            <MDDropzone
                                options={{
                                    addRemoveLinks: true,
                                    disablePreviews: false
                                }}
                                onFileAdd={onFileAdd}
                            />
                        ),
                        []
                    )}
                </Grid>
                <Grid item xs={3} sm={3} display="flex" alignItems="flex-end">
                    <MDButton
                        variant="contained"
                        color="primary"
                        onClick={uploadFiles}
                    >
                        Upload Files
                    </MDButton>
                </Grid>
            </Grid>


        </MDBox>
    )
}